import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiMessageCircle, FiX, FiSend, FiChevronLeft, FiAlertTriangle, FiCheck, FiCheckCircle, FiImage } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import chatService from '../../services/chat.service';
import { connectSocket, getSocket, disconnectSocket } from '../../services/socket.service';

const ChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const messagesEnd = useRef(null);
  const typingTimeout = useRef(null);

  const isCustomer = ['customer', 'affiliate'].includes(user?.role);
  const isMerchant = ['merchant', 'merchant_staff'].includes(user?.role);
  const isSupport = ['support', 'admin', 'super_admin'].includes(user?.role);
  const canChat = isCustomer || isMerchant || isSupport;

  // ── SOCKET CONNECTION ──
  useEffect(() => {
    if (!isAuthenticated || !canChat) return;
    const token = localStorage.getItem('ebuywiz_token');
    if (!token) return;

    const sock = connectSocket(token);

    sock.on('new-message', ({ conversationId, message }) => {
      if (activeConvo?._id === conversationId) {
        setMessages(prev => [...prev, message]);
        sock.emit('mark-read', { conversationId });
      }
      setConversations(prev => prev.map(c =>
        c._id === conversationId
          ? { ...c, lastMessage: message.content, lastMessageAt: message.createdAt, myUnread: activeConvo?._id === conversationId ? 0 : (c.myUnread || 0) + 1 }
          : c
      ));
      updateUnread();
    });

    sock.on('typing', ({ conversationId, userName }) => {
      if (activeConvo?._id === conversationId) {
        setTyping(userName);
      }
    });

    sock.on('stop-typing', ({ conversationId }) => {
      if (activeConvo?._id === conversationId) {
        setTyping(null);
      }
    });

    sock.on('messages-read', ({ conversationId }) => {
      if (activeConvo?._id === conversationId) {
        setMessages(prev => prev.map(m => ({ ...m, readBy: [...(m.readBy || []), 'other'] })));
      }
    });

    sock.on('unread-update', () => updateUnread());

    return () => {
      sock.off('new-message');
      sock.off('typing');
      sock.off('stop-typing');
      sock.off('messages-read');
      sock.off('unread-update');
    };
  }, [isAuthenticated, canChat, activeConvo?._id]);

  // ── FETCH CONVERSATIONS ──
  const fetchConversations = useCallback(async () => {
    if (!canChat) return;
    try {
      const res = await chatService.getConversations({ status: 'active' });
      setConversations(res.data.data.conversations);
    } catch { /* silent */ }
  }, [canChat]);

  const updateUnread = useCallback(async () => {
    try {
      const res = await chatService.getUnreadCount();
      setUnreadTotal(res.data.data.total);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (isAuthenticated && canChat) {
      fetchConversations();
      updateUnread();
    }
  }, [isAuthenticated, canChat, fetchConversations, updateUnread]);

  // Listen for open-chat events from ContactSellerButton
  useEffect(() => {
    const handler = async (e) => {
      const { conversationId } = e.detail || {};
      if (!conversationId) { setOpen(true); return; }
      setOpen(true);
      await fetchConversations();
      try {
        const res = await chatService.getConversation(conversationId);
        const data = res.data.data;
        setActiveConvo(data);
        setMessages(data.messages || []);
        getSocket()?.emit('mark-read', { conversationId });
        updateUnread();
      } catch { /* silent */ }
    };
    window.addEventListener('open-chat', handler);
    return () => window.removeEventListener('open-chat', handler);
  }, [fetchConversations, updateUnread]);

  // ── OPEN CONVERSATION ──
  const openConvo = async (convo) => {
    setLoading(true);
    try {
      const res = await chatService.getConversation(convo._id);
      const data = res.data.data;
      setActiveConvo(data);
      setMessages(data.messages || []);
      getSocket()?.emit('mark-read', { conversationId: convo._id });
      setConversations(prev => prev.map(c => c._id === convo._id ? { ...c, myUnread: 0 } : c));
      updateUnread();
    } catch { /* silent */ }
    setLoading(false);
  };

  // ── SEND MESSAGE ──
  const sendMessage = () => {
    if (!input.trim() || !activeConvo) return;
    const sock = getSocket();
    if (!sock) return;

    sock.emit('send-message', {
      conversationId: activeConvo._id,
      content: input.trim(),
    }, (response) => {
      if (response?.error) console.error(response.error);
    });

    sock.emit('stop-typing', { conversationId: activeConvo._id });
    setInput('');
  };

  // ── TYPING INDICATOR ──
  const handleInputChange = (e) => {
    setInput(e.target.value);
    const sock = getSocket();
    if (!sock || !activeConvo) return;

    sock.emit('typing', { conversationId: activeConvo._id });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      sock.emit('stop-typing', { conversationId: activeConvo._id });
    }, 2000);
  };

  // ── ACTIONS ──
  const escalate = async () => {
    if (!activeConvo) return;
    try {
      await chatService.escalateConversation(activeConvo._id);
      setActiveConvo(prev => ({ ...prev, escalatedToSupport: true }));
      fetchConversations();
    } catch { /* silent */ }
  };

  const resolve = async () => {
    if (!activeConvo) return;
    try {
      await chatService.resolveConversation(activeConvo._id);
      setActiveConvo(null);
      setMessages([]);
      fetchConversations();
    } catch { /* silent */ }
  };

  // ── AUTO SCROLL ──
  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  if (!isAuthenticated || !canChat) return null;

  const otherName = isCustomer
    ? (activeConvo?.storeName || activeConvo?.merchantName)
    : activeConvo?.customerName;

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => { setOpen(!open); if (!open) fetchConversations(); }}
        style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9998,
          width: '56px', height: '56px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #B91C1C 0%, #991B1B 100%)',
          color: '#FFF', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(185,28,28,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
      >
        {open ? <FiX size={22} /> : <FiMessageCircle size={22} />}
        {!open && unreadTotal > 0 && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px',
            background: '#EAB308', color: '#1F2937', fontWeight: 800,
            fontSize: '11px', width: '22px', height: '22px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{unreadTotal > 9 ? '9+' : unreadTotal}</span>
        )}
      </button>

      {/* Chat Panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: '90px', right: '24px', zIndex: 9999,
          width: '380px', maxWidth: 'calc(100vw - 32px)', height: '520px', maxHeight: 'calc(100vh - 120px)',
          background: '#FFF', borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid #E5E7EB',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px', background: 'linear-gradient(135deg, #B91C1C, #991B1B)',
            color: '#FFF', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0,
          }}>
            {activeConvo && (
              <button onClick={() => { setActiveConvo(null); setMessages([]); setTyping(null); }}
                style={{ background: 'none', border: 'none', color: '#FFF', cursor: 'pointer', padding: 0 }}>
                <FiChevronLeft size={20} />
              </button>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>
                {activeConvo ? otherName : 'Messages'}
              </div>
              {activeConvo?.productName && (
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Re: {activeConvo.productName}</div>
              )}
              {activeConvo?.orderId && (
                <div style={{ fontSize: '11px', opacity: 0.8 }}>Order #{activeConvo.orderId}</div>
              )}
            </div>
            {activeConvo && (
              <div style={{ display: 'flex', gap: '6px' }}>
                {!activeConvo.escalatedToSupport && isCustomer && (
                  <button onClick={escalate} title="Escalate to support"
                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#FFF', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>
                    <FiAlertTriangle size={12} />
                  </button>
                )}
                <button onClick={resolve} title="Resolve"
                  style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#FFF', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }}>
                  <FiCheckCircle size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {!activeConvo ? (
              /* Conversation List */
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                {conversations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
                    <FiMessageCircle size={36} style={{ marginBottom: '12px', opacity: 0.4 }} />
                    <p style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 4px' }}>No conversations yet</p>
                    <p style={{ fontSize: '12px', margin: 0 }}>
                      {isCustomer ? 'Start a chat from any product or order page' : 'Customer messages will appear here'}
                    </p>
                  </div>
                ) : (
                  conversations.map(c => (
                    <button
                      key={c._id}
                      onClick={() => openConvo(c)}
                      style={{
                        width: '100%', padding: '12px', background: c.myUnread > 0 ? '#FEF2F2' : '#FAFAFA',
                        border: '1px solid #F3F4F6', borderRadius: '10px', marginBottom: '6px',
                        cursor: 'pointer', textAlign: 'left', display: 'flex', gap: '10px', alignItems: 'center',
                      }}
                    >
                      {c.productImage ? (
                        <img src={c.productImage} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '8px',
                          background: 'linear-gradient(135deg, #E5E7EB, #D1D5DB)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '16px', fontWeight: 700, color: '#6B7280',
                        }}>
                          {(isCustomer ? c.merchantName : c.customerName)?.[0] || '?'}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '13px', color: '#1F2937' }}>
                            {isCustomer ? (c.storeName || c.merchantName) : c.customerName}
                          </span>
                          {c.myUnread > 0 && (
                            <span style={{
                              background: '#B91C1C', color: '#FFF', fontSize: '10px', fontWeight: 700,
                              padding: '1px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center',
                            }}>{c.myUnread}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.productName && <span style={{ color: '#B91C1C', fontWeight: 500 }}>{c.productName} · </span>}
                          {c.lastMessage}
                        </div>
                        <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '2px' }}>
                          {c.lastMessageAt && new Date(c.lastMessageAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {c.escalatedToSupport && <span style={{ color: '#D97706', marginLeft: '8px' }}>⚠ Escalated</span>}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            ) : (
              /* Messages View */
              <>
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px' }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF', fontSize: '13px' }}>Loading...</div>
                  ) : (
                    messages.map((msg, i) => {
                      const isMe = msg.senderId === user?.id;
                      const isSystem = msg.type === 'system';
                      if (isSystem) {
                        return (
                          <div key={msg._id || i} style={{
                            textAlign: 'center', margin: '8px 0', fontSize: '11px', color: '#9CA3AF',
                            background: '#F9FAFB', padding: '4px 12px', borderRadius: '10px', display: 'inline-block',
                            width: '100%',
                          }}>
                            {msg.content}
                          </div>
                        );
                      }
                      return (
                        <div key={msg._id || i} style={{
                          display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start',
                          marginBottom: '8px',
                        }}>
                          <div style={{
                            maxWidth: '75%', padding: '10px 14px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                            background: isMe ? '#B91C1C' : '#F3F4F6', color: isMe ? '#FFF' : '#1F2937',
                          }}>
                            {!isMe && (
                              <div style={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', marginBottom: '2px' }}>
                                {msg.senderName}
                              </div>
                            )}
                            <div style={{ fontSize: '13px', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.content}</div>
                            <div style={{
                              fontSize: '10px', marginTop: '4px',
                              color: isMe ? 'rgba(255,255,255,0.6)' : '#9CA3AF', textAlign: 'right',
                              display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px',
                            }}>
                              {msg.createdAt && new Date(msg.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                              {isMe && (msg.readBy?.length > 1 ? <FiCheck size={10} color="#4ADE80" /> : <FiCheck size={10} />)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {typing && (
                    <div style={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', padding: '4px 0' }}>
                      {typing} is typing...
                    </div>
                  )}
                  <div ref={messagesEnd} />
                </div>

                {/* Input */}
                {activeConvo.status === 'active' && (
                  <div style={{
                    padding: '10px 12px', borderTop: '1px solid #F3F4F6',
                    display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0,
                  }}>
                    <input
                      value={input}
                      onChange={handleInputChange}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Type a message..."
                      style={{
                        flex: 1, padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '24px',
                        fontSize: '13px', outline: 'none', fontFamily: 'inherit',
                      }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!input.trim()}
                      style={{
                        width: '38px', height: '38px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                        background: input.trim() ? '#B91C1C' : '#E5E7EB',
                        color: input.trim() ? '#FFF' : '#9CA3AF',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s',
                      }}
                    >
                      <FiSend size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
